/**
 * Plan detection (Oct 2026, design_handoff_plan_detection) — the pure half. A chat that settles
 * on a plan gets the viewer ONE private suggestion (`PlanDraftDto`); these helpers turn it into
 * the bar's line, the sheet's rows, the dotted highlights on the messages it read, the composer's
 * `fromChat` state and the event wizard's prefill. No DOM here; everything is unit-testable.
 */
import { EVENT_CATEGORIES, MAX_EVENT_CATEGORIES } from '../../../constants/eventCategories';
import { DEFAULT_WINDOW, DEFAULT_HOURS, dayDate } from '../../../utils/portalWindow';

/** The handoff's colours. The ring itself comes from wallTokens. */
export const PLAN = {
  pink: '#FD199F',
  pinkFill: '#FFF0F8',
  pinkSoft: '#FFCCE6',
  cyan: '#22ECFB',
  magenta: '#FF00AA',
};

export const EASE = 'cubic-bezier(.2,.7,.2,1)';

/** `PLAN DETECTED`, or `PLAN FOUND` after a manual run. */
export function labelFor(draft) {
  return draft?.source === 'manual' ? 'PLAN FOUND' : 'PLAN DETECTED';
}

export const LOOK_BACK = [
  { key: 'messages', label: 'LAST 20 MSGS', reading: 'THE LAST 20 MESSAGES' },
  { key: 'hour', label: 'LAST HOUR', reading: 'THE LAST HOUR' },
  { key: 'today', label: 'TODAY', reading: 'TODAY' },
];

export function lookBackReading(key) {
  return (LOOK_BACK.find((r) => r.key === key) || LOOK_BACK[0]).reading;
}

/** The next window out: messages → hour → today → null (nowhere further to look). */
export function nextLookBack(key) {
  const i = LOOK_BACK.findIndex((r) => r.key === key);
  return i >= 0 && i < LOOK_BACK.length - 1 ? LOOK_BACK[i + 1].key : null;
}

// ── Dates ───────────────────────────────────────────────────────────────

const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** "yyyy-MM-ddTHH:mm" wall-clock → a local Date, or null. */
export function parseLocal(s) {
  if (!s || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return null;
  const d = new Date(s.slice(0, 16));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtTime(d) {
  const h = d.getHours(); const m = d.getMinutes();
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ':00'} ${h < 12 ? 'AM' : 'PM'}`;
}

/** `SAT OCT 10 · 6:00 PM` */
export function fmtWhen(s) {
  const d = parseLocal(s);
  if (!d) return '';
  return `${DOW[d.getDay()]} ${MON[d.getMonth()]} ${d.getDate()} · ${fmtTime(d)}`;
}

/** `Sat 6:00 PM` — the bar's short form. */
export function fmtShortWhen(s) {
  const d = parseLocal(s);
  if (!d) return '';
  const dow = DOW[d.getDay()];
  return `${dow[0]}${dow.slice(1).toLowerCase()} ${fmtTime(d)}`;
}

/** `6 → 10 PM` for the spec line; `6 PM →` with no end. */
export function fmtSpan(start, end) {
  const a = parseLocal(start);
  if (!a) return '';
  const b = parseLocal(end);
  const hour = (d) => `${d.getHours() % 12 || 12}${d.getMinutes() ? `:${String(d.getMinutes()).padStart(2, '0')}` : ''}`;
  const ampm = (d) => (d.getHours() < 12 ? 'AM' : 'PM');
  if (!b) return `${hour(a)} ${ampm(a)} →`;
  return ampm(a) === ampm(b) ? `${hour(a)} → ${hour(b)} ${ampm(b)}` : `${hour(a)} ${ampm(a)} → ${hour(b)} ${ampm(b)}`;
}

// ── The bar + the sheet ─────────────────────────────────────────────────

/** `Sunset at Paul's Tomb · Sat 6:00 PM` */
export function barText(draft) {
  const when = fmtShortWhen(draft?.startLocal);
  return [draft?.title, when].filter(Boolean).join(' · ');
}

export function placeLine(draft) {
  const p = draft?.place;
  if (!p) return '';
  if (p.matched && p.name) {
    const first = (p.address || '').split(',')[0].trim();
    return first && first.toLowerCase() !== p.name.toLowerCase() ? `${p.name} · ${first}` : p.name;
  }
  return p.text || p.query || '';
}

export function money(cents) {
  if (cents == null) return '';
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

/** The WHAT IT FOUND rows. Missing values read NOT MENTIONED. */
export function whatItFound(draft, viewerUserId = null) {
  const people = draft?.participants || [];
  const names = people.map((p) => (p.userId === viewerUserId || p.isViewer ? 'YOU' : (p.displayName || '').split(' ')[0].toUpperCase())).filter(Boolean);
  const mentioned = (draft?.mentionedNames || []).map((n) => n.toUpperCase());
  const row = (k, v) => ({ key: k, value: v || null, missing: !v });
  const who = names.length ? `${names.length} PEOPLE · ${names.join(', ')}${mentioned.length ? ` + ${mentioned.join(', ')}` : ''}` : (mentioned.length ? mentioned.join(', ') : '');
  const src = (field) => (draft?.sources || []).find((s) => s.field === field);
  const check = (field, text) => (text && src(field)?.needsCheck ? `${text}` : text);
  return [
    row('WHEN', check('start', fmtWhen(draft?.startLocal) ? `${fmtWhen(draft.startLocal)}${src('start')?.needsCheck ? ' · AROUND' : ''}` : '')),
    row('WHERE', placeLine(draft).toUpperCase()),
    row('WHO', who),
    row('SIZE', draft?.size ? `UNDER ${draft.size}` : ''),
    row('COST', draft?.costCents ? `${money(draft.costCents)} EACH` : ''),
    row('BRING', (draft?.bring || '').toUpperCase()),
  ];
}

/** The sheet's one sentence. */
export function summaryLine(draft) {
  if (draft?.summary) return draft.summary;
  return 'A plan is forming. Fill in the details next.';
}

/** `[ PLAN DETECTED · 7 MESSAGES IN LAKE CREW ]` */
export function kickerFor(draft) {
  const n = draft?.messageCount || 0;
  return `[ ${labelFor(draft)} · ${n} MESSAGE${n === 1 ? '' : 'S'} IN ${(draft?.threadName || 'THIS CHAT').toUpperCase()} ]`;
}

/** The Portal path's defaults: a size cap ⇒ CLOSE FRIENDS + CURATED; otherwise FRIENDS, open. */
export function portalDefaults(draft) {
  const small = !!draft?.size;
  return { aud: small ? ['close'] : ['friends'], joinMode: small ? 'curated' : 'open' };
}

/** `CASUAL · CLOSE FRIENDS · CURATED · 6 → 10 PM` */
export function portalSpec(draft) {
  const { aud, joinMode } = portalDefaults(draft);
  const audLabel = aud.includes('close') ? 'CLOSE FRIENDS' : 'FRIENDS';
  const span = fmtSpan(draft?.startLocal, draft?.endLocal || defaultEndLocal(draft?.startLocal));
  return ['CASUAL', audLabel, joinMode.toUpperCase(), span || null].filter(Boolean).join(' · ');
}

/** `EVENT PAGE · PRIVATE · $15 TICKET` */
export function eventSpec(draft) {
  return ['EVENT PAGE', 'PRIVATE', draft?.costCents ? `${money(draft.costCents)} TICKET` : 'FREE RSVP'].join(' · ');
}

/** `YOU OPENED A PORTAL FROM THIS CHAT` / `YOU CREATED AN EVENT FROM THIS CHAT` */
export function publishedLine(kind, mine = true) {
  const who = mine ? 'YOU' : null;
  const verb = kind === 'event' ? 'CREATED AN EVENT' : 'OPENED A PORTAL';
  return who ? `${who} ${verb} FROM THIS CHAT` : `${verb} FROM THIS CHAT`;
}

// ── Highlights ──────────────────────────────────────────────────────────

/** messageId → the quoted phrases the model pulled from it. */
export function highlightsFor(draft) {
  const map = new Map();
  if (!draft || draft.status !== 'suggested') return map;
  for (const s of draft.sources || []) {
    if (!s.sourceMessageId || !s.quote) continue;
    const list = map.get(s.sourceMessageId) || [];
    if (!list.some((q) => q.toLowerCase() === s.quote.toLowerCase())) list.push(s.quote);
    map.set(s.sourceMessageId, list);
  }
  return map;
}

/** Split a message into plain and highlighted parts (case-insensitive, first occurrence of each quote). */
export function splitHighlights(text, quotes) {
  if (!text) return [];
  if (!quotes || quotes.length === 0) return [{ t: text, on: false }];
  const lower = text.toLowerCase();
  const spans = [];
  for (const q of quotes) {
    const at = lower.indexOf(q.toLowerCase());
    if (at < 0) continue;
    spans.push([at, at + q.length]);
  }
  spans.sort((a, b) => a[0] - b[0]);
  const parts = [];
  let cur = 0;
  for (const [a, b] of spans) {
    if (a < cur) continue; // overlapping quotes: the first wins
    if (a > cur) parts.push({ t: text.slice(cur, a), on: false });
    parts.push({ t: text.slice(a, b), on: true });
    cur = b;
  }
  if (cur < text.length) parts.push({ t: text.slice(cur), on: false });
  return parts;
}

// ── Into the composer ───────────────────────────────────────────────────

function defaultEndLocal(startLocal) {
  const d = parseLocal(startLocal);
  if (!d) return null;
  d.setHours(d.getHours() + DEFAULT_HOURS);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A Portal window from the plan's wall-clock times (`utils/portalWindow` shape). */
export function windowFor(startLocal, endLocal, now = new Date()) {
  const s = parseLocal(startLocal);
  if (!s) return DEFAULT_WINDOW;
  const today = dayDate(0, now);
  const day = Math.max(0, Math.round((dayDate(0, s) - today) / 86400000));
  const start = s.getHours();
  const e = parseLocal(endLocal);
  let endAbs = day * 24 + start + DEFAULT_HOURS;
  if (e && e > s) {
    const eDay = Math.max(0, Math.round((dayDate(0, e) - today) / 86400000));
    endAbs = Math.max(day * 24 + start + 1, eDay * 24 + e.getHours() + (e.getMinutes() >= 30 ? 1 : 0));
  }
  return { on: true, day, start, endAbs };
}

/** The sources sheet's rows: field, value, quote, sender · time, tag. */
export function sourceRows(draft) {
  const label = { title: 'WORDS', start: 'WINDOW', end: 'ENDS', place: 'VENUE', size: 'SIZE', cost: 'COST', bring: 'BRING' };
  return (draft?.sources || []).map((s) => ({
    key: s.field,
    field: label[s.field] || s.field.toUpperCase(),
    value: s.field === 'start' ? fmtWhen(s.value) || s.value : s.field === 'cost' ? `${money(Number(s.value))} EACH` : (s.value || ''),
    quote: s.quote || '',
    src: [s.senderName ? s.senderName.toUpperCase() : null, s.sentAt ? fmtTime(new Date(s.sentAt)) : null].filter(Boolean).join(' · '),
    tag: s.field === 'place' && draft?.place?.matched ? 'MATCHED ON MAP' : s.needsCheck ? 'CHECK' : s.field === 'title' ? 'SUGGESTED' : 'FROM CHAT',
  }));
}

/**
 * `navigate('/portals/new', { state: { fromChat } })` — the composer opens on OPEN (step 3) with
 * everything filled in. `inviteUserIds` is everyone else in the chat; the panel lets them be
 * un-ticked.
 */
export function fromChatState(draft, viewerUserId, { cityFallback = null } = {}) {
  const { aud, joinMode } = portalDefaults(draft);
  const p = draft?.place;
  const venue = p?.matched && p.lat != null && p.lng != null ? { name: p.name, address: p.address || '', lat: p.lat, lng: p.lng } : null;
  const city = p?.matched && p.city ? { label: p.city, lat: p.lat ?? null, lng: p.lng ?? null } : cityFallback;
  return {
    threadId: draft.threadId,
    threadName: draft.threadName,
    text: draft.title || '',
    city,
    venue,
    venueOpen: false,
    window: windowFor(draft.startLocal, draft.endLocal),
    aud,
    joinMode,
    clubIds: [],
    inviteUserIds: (draft.participants || []).filter((x) => x.userId !== viewerUserId && !x.isViewer).map((x) => x.userId),
    people: (draft.participants || []).filter((x) => x.userId !== viewerUserId && !x.isViewer).map((x) => ({ userId: x.userId, name: x.displayName || '', avatarUrl: x.avatarUrl || null })),
    sources: sourceRows(draft),
  };
}

// ── Into the event wizard ───────────────────────────────────────────────

/** The AI's category slugs, checked against the real list, at most three. */
export function suggestedCategories(draft) {
  const valid = new Set(EVENT_CATEGORIES.map((c) => c.slug));
  return (draft?.categories || []).filter((s) => valid.has(s)).slice(0, MAX_EVENT_CATEGORIES);
}

/** The field tags (handoff §4): FROM CHAT / MATCHED ON MAP cyan · CHECK pink · SUGGESTED black · NEEDED magenta · OPTIONAL / DEFAULT grey. */
export function tagTone(tag) {
  switch (tag) {
    case 'FROM CHAT': case 'MATCHED ON MAP': case 'PICKED': return { bg: PLAN.cyan, fg: '#000' };
    case 'CHECK': return { bg: PLAN.pinkSoft, fg: '#000' };
    case 'SUGGESTED': return { bg: '#000', fg: '#fff' };
    case 'NEEDED': return { bg: PLAN.magenta, fg: '#fff' };
    default: return { bg: '#F2F2F2', fg: '#6B7280' };
  }
}

/**
 * What the wizard starts with. `formData` patches `EventCreatePage`'s state; `tags` names the
 * tag per field; `quotes` carries the source line under it; the rest is sibling state.
 */
export function eventPrefill(draft) {
  const src = (field) => (draft?.sources || []).find((s) => s.field === field);
  const p = draft?.place;
  const matched = !!(p?.matched && p.lat != null && p.lng != null);
  const cats = suggestedCategories(draft);
  const paid = !!draft?.costCents;
  const formData = {
    title: draft?.title || '',
    description: draft?.description ? `<p>${draft.description}</p>` : '',
    dateTime: draft?.startLocal || '',
    endDateTime: draft?.endLocal || '',
    mode: draft?.size ? 'curated' : 'open',
    isPrivate: true,
    categories: cats,
    category: cats[0] || '',
    fundingType: 'guaranteed',
    ticketPrice: paid ? (draft.costCents / 100).toFixed(2) : '',
    location: matched ? (p.address || p.name || '') : '',
    locationLat: matched ? p.lat : null,
    locationLng: matched ? p.lng : null,
    locationCity: matched ? (p.city || '') : '',
    venueName: matched ? (p.name || '') : '',
    venueAddress: matched ? (p.address || '') : '',
    venueLat: matched ? p.lat : null,
    venueLng: matched ? p.lng : null,
  };
  const quoteFor = (field) => {
    const s = src(field);
    return s?.quote ? { quote: s.quote, src: [s.senderName?.toUpperCase(), s.sentAt ? fmtTime(new Date(s.sentAt)) : null].filter(Boolean).join(' · ') } : null;
  };
  const tags = {
    title: draft?.title ? 'SUGGESTED' : 'NEEDED',
    dateTime: draft?.startLocal ? (src('start')?.needsCheck ? 'CHECK' : 'FROM CHAT') : 'NEEDED',
    endDateTime: draft?.endLocal ? (src('end')?.needsCheck ? 'CHECK' : 'FROM CHAT') : 'OPTIONAL',
    mode: draft?.size ? 'SUGGESTED' : 'DEFAULT',
    categories: cats.length ? 'SUGGESTED' : 'NEEDED',
    isPrivate: 'SUGGESTED',
    location: matched ? 'MATCHED ON MAP' : 'NEEDED',
    venueName: matched ? 'MATCHED ON MAP' : 'OPTIONAL',
    description: draft?.description ? 'SUGGESTED' : 'NEEDED',
    ticketPrice: paid ? 'CHECK' : 'DEFAULT',
    maxAttendees: draft?.size ? 'FROM CHAT' : 'OPTIONAL',
    partySupplies: draft?.bring ? 'FROM CHAT' : 'OPTIONAL',
  };
  const quotes = {
    title: quoteFor('title'),
    dateTime: quoteFor('start'),
    endDateTime: quoteFor('end'),
    location: quoteFor('place'),
    venueName: quoteFor('place'),
    ticketPrice: quoteFor('cost'),
    maxAttendees: quoteFor('size'),
    mode: quoteFor('size'),
    partySupplies: quoteFor('bring'),
  };
  return {
    formData,
    tags,
    quotes,
    isFree: !paid,
    maxAttendees: draft?.size ? String(draft.size) : '',
    partySupplies: draft?.bring ? draft.bring.split(/,\s*/).filter(Boolean) : [],
    threadName: draft?.threadName || '',
    inviteUserIds: (draft?.participants || []).filter((x) => !x.isViewer).map((x) => x.userId),
  };
}
